import NotarialCalendar from './NotarialCalendar';
import { guardAdminNavigation, useAdminDirty } from './UnsavedChanges';
import { useRoleViewFetch } from './RoleViewContext';
import { notaryProcessState } from './notaryProcessState';
// src/components/admin/NotarioDashboard.js
// Dashboard del Notario (Parte 7, 13, 14, 15, 16, 17).
// Reutiliza el sistema de auth y estilos de Wisby. El notario solo ve y
// gestiona los expedientes que le fueron asignados.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './NotarioDashboard.css';
import { createPortal } from 'react-dom';
import logoWisby from '../../assets/logo.png';
import NotarialWorkflow, { NOTARIAL_STAGES } from './NotarialWorkflow';
import PowerSignatureProgress from '../poderes/PowerSignatureProgress';
import { PHYSICAL_DEED_TITLE } from './SuggestedPhysicalDeed';

// Constante `API_URL` que centraliza un valor de configuración compartido.
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
// Función `getAuthToken` que obtiene la información necesaria para el flujo.
const getAuthToken = () => localStorage.getItem('token') || sessionStorage.getItem('token');

const ATTACHMENT_LABELS = {
  incorporationReforms: 'Acta constitutiva y reformas', currentCapital: 'Acta del capital social',
  currentManagement: 'Órgano de administración', grantorOfficialId: 'Identificación del otorgante',
  granteeOfficialId: 'Identificación del apoderado', grantorTaxStatusCertificate: 'Constancia fiscal del otorgante',
  granteeTaxStatusCertificate: 'Constancia fiscal del apoderado', granteeCurp: 'CURP del apoderado',
  marriageCertificate: 'Acta de matrimonio', spouseOfficialId: 'Identificación del cónyuge',
};

// Catálogos (espejo del backend)
const CHECKLIST_LABELS = {
  acta_firmada: 'Acta firmada',
  acta_constitutiva: 'Acta constitutiva',
  reformas: 'Reformas',
  estatutos: 'Estatutos',
  designacion_organo: 'Designación del órgano',
  rfc_sociedad: 'RFC Sociedad',
  rfc_delegado: 'RFC Delegado',
  curp_delegado: 'CURP Delegado',
  ine_pasaporte: 'INE / Pasaporte',
  lista_apoderados: 'Lista de apoderados',
  tipo_poder: 'Tipo de poder',
};

// Constante `PODER_LABELS` que centraliza un valor de configuración compartido.
const PODER_LABELS = {
  general_admin: 'Poder General de Administración', pleitos_cobranzas: 'Poder General para Pleitos y Cobranzas',
  dominio: 'Poder General de Dominio', admin_tramites: 'Poder Especial para Trámites Administrativos',
  articulo_35_lgtoc: 'Poder Especial Artículo 35 LGTOC', especial_dominio: 'Poder Especial de Dominio',
  admin_limitadas: 'Poder para Actos de Administración con Facultades Limitadas',
  revocacion: 'Revocación de Poderes', otro: 'Otro',
};

// Constante `ORGANOS` que centraliza un valor de configuración compartido.
const ORGANOS = {
  consejo: 'Consejo de Administración', administrador_unico: 'Administrador Único',
  asamblea: 'Asamblea de Accionistas', apoderado: 'Apoderado', no_seguro: 'No estoy seguro',
};

// Acciones permitidas al notario (Parte 16) según estado actual
const ACTIONS_BY_STATUS = {
  en_revision_notarial: [
    { action: 'notario_request_corrections', label: 'Solicitar correcciones', kind: 'warn' },
    { action: 'notario_mark_complete', label: 'Marcar expediente completo', kind: 'success' },
  ],
  correcciones_solicitadas_notario: [
    { action: 'notario_resume_review', label: 'Reanudar revisión', kind: 'primary' },
  ],
  expediente_completo: [
    { action: 'notario_request_corrections', label: 'Solicitar correcciones', kind: 'warn' },
    { action: 'notario_ready_protocolizacion', label: 'Marcar listo para protocolización', kind: 'success' },
  ],
  listo_protocolizacion: [
    { action: 'notario_mark_protocolizado', label: 'Marcar protocolizado', kind: 'success' },
  ],
  protocolizado: [
    { action: 'notario_register_rppc_siger', label: 'Registrar inscripción RPPC/SIGER', kind: 'primary' },
  ],
  inscrito_rppc_siger: [
    { action: 'notario_finalize', label: 'Marcar expediente finalizado', kind: 'success' },
  ],
};

// ==================== ICONOS ====================
const Icon = ({ name }) => {
  // Configuración `common` que agrupa valores relacionados.
  const common = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'dashboard': return <svg {...common}><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>;
    case 'folder': return <svg {...common}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>;
    case 'clock': return <svg {...common}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>;
    case 'user': return <svg {...common}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>;
    case 'logout': return <svg {...common}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>;
    case 'check': return <svg {...common}><polyline points="20 6 9 17 4 12" /></svg>;
    case 'eye': return <svg {...common}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>;
    case 'close': return <svg {...common}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>;
    case 'briefcase': return <svg {...common}><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>;
    default: return null;
  }
};

// Constante `PRIORIDAD_LABELS` que centraliza un valor de configuración compartido.
const PRIORIDAD_LABELS = {
  baja: { label: 'Baja', class: 'prio-baja' },
  media: { label: 'Media', class: 'prio-media' },
  alta: { label: 'Alta', class: 'prio-alta' },
};

// Constante `STATUS_LABELS_DEFAULT` que centraliza un valor de configuración compartido.
const STATUS_LABELS_DEFAULT = {
  pendiente_revision_interna: 'Pendiente de revisión interna',
  correcciones_solicitadas_admin: 'Correcciones solicitadas por administrador',
  aprobado_admin: 'Aprobado por administrador',
  pendiente_asignacion_notario: 'Pendiente de asignación',
  asignado_notario: 'Asignado al notario',
  en_revision_notarial: 'En revisión notarial',
  correcciones_solicitadas_notario: 'Correcciones solicitadas por notario',
  expediente_completo: 'Expediente completo',
  listo_protocolizacion: 'Listo para protocolización',
  protocolizado: 'Protocolizado',
  inscrito_rppc_siger: 'Inscrito RPPC/SIGER',
  finalizado: 'Finalizado',
};

// ================================================================
// COMPONENTE PRINCIPAL
// ================================================================
export default function NotarioDashboard({ currentUser, onLogout, onNavigate, adminView = false }) {
  const fetch = useRoleViewFetch();
  // Estado local `tab` y su actualizador, usados para reflejar cambios en la interfaz.
  const [tab, setTab] = useState('expedientes'); // dashboard | expedientes | historial | perfil
  // Estado local `expedientes` y su actualizador, usados para reflejar cambios en la interfaz.
  const [expedientes, setExpedientes] = useState([]);
  // Estado local `stats` y su actualizador, usados para reflejar cambios en la interfaz.
  const [stats, setStats] = useState({ total: 0, pendientes: 0, enRevision: 0, completos: 0, finalizados: 0 });
  // Estado local `me` y su actualizador, usados para reflejar cambios en la interfaz.
  const [me, setMe] = useState({ notario: null, user: null });
  // Estado local `statusLabels` y su actualizador, usados para reflejar cambios en la interfaz.
  const [statusLabels, setStatusLabels] = useState(STATUS_LABELS_DEFAULT);
  // Estado local `loading` y su actualizador, usados para reflejar cambios en la interfaz.
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  // Estado local `notif` y su actualizador, usados para reflejar cambios en la interfaz.
  const [notif, setNotif] = useState(null);
  // Estado local `detail` y su actualizador, usados para reflejar cambios en la interfaz.
  const [detail, setDetail] = useState({ open: false, poder: null, loading: false });

  // Callback `showNotif` memoizado para conservar su referencia entre renderizados.
  const showNotif = useCallback((message, type = 'success') => {
    setNotif({ message, type });
    setTimeout(() => setNotif(null), 4000);
  }, []);

  // Callback `authHeaders` memoizado para conservar su referencia entre renderizados.
  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'x-auth-token': getAuthToken(),
  }), []);

  // Callback `fetchMe` memoizado para conservar su referencia entre renderizados.
  const fetchMe = useCallback(async () => {
    try {
      // Valor intermedio `res` utilizado por la lógica de este bloque.
      const res = await fetch(`${API_URL}/api/notario-dashboard/me`, { headers: { 'x-auth-token': getAuthToken() } });
      if (res.ok) { const data = await res.json(); setMe(data); }
    } catch { /* silencioso */ }
  }, [fetch]);

  // Callback `fetchStats` memoizado para conservar su referencia entre renderizados.
  const fetchStats = useCallback(async () => {
    try {
      // Valor intermedio `res` utilizado por la lógica de este bloque.
      const res = await fetch(`${API_URL}/api/notario-dashboard/stats`, { headers: { 'x-auth-token': getAuthToken() } });
      if (res.ok) setStats(await res.json());
    } catch { /* silencioso */ }
  }, [fetch]);

  // Callback `fetchExpedientes` memoizado para conservar su referencia entre renderizados.
  const fetchExpedientes = useCallback(async () => {
    try {
      setLoading(true);
      setListError('');
      // Valor intermedio `res` utilizado por la lógica de este bloque.
      const res = await fetch(`${API_URL}/api/notario-dashboard/expedientes`, { headers: { 'x-auth-token': getAuthToken() } });
      if (res.ok) {
        // Valor intermedio `data` utilizado por la lógica de este bloque.
        const data = await res.json();
        setExpedientes(data.poderes || []);
        if (data.statusLabels) setStatusLabels(data.statusLabels);
      } else {
        const data = await res.json();
        setListError(data.msg || 'No se pudieron cargar los folios.');
      }
    } catch { setListError('No se pudieron cargar los folios. Revisa tu conexión.'); }
    finally { setLoading(false); }
  }, [fetch]);

  useEffect(() => {
    fetchMe(); fetchStats(); fetchExpedientes();
  }, [fetchMe, fetchStats, fetchExpedientes]);

  // Historial agregado de todos los expedientes (Parte 17, vista Historial)
  const timelineGlobal = useMemo(() => {
    // Colección `events` que reúne los elementos utilizados en este flujo.
    const events = [];
    expedientes.forEach(p => {
      (p.history || []).forEach(h => events.push({ ...h, companyName: p.companyName, poderId: p._id, folio: p.folio }));
    });
    return events.sort((a, b) => new Date(b.performedAt) - new Date(a.performedAt)).slice(0, 100);
  }, [expedientes]);

  // Callback `openDetail` memoizado para conservar su referencia entre renderizados.
  const openDetail = useCallback(async (poder) => {
    setDetail({ open: true, poder, loading: true });
    try {
      // Valor intermedio `res` utilizado por la lógica de este bloque.
      const res = await fetch(`${API_URL}/api/notario-dashboard/expedientes/${poder._id}`, { headers: { 'x-auth-token': getAuthToken() } });
      // Valor intermedio `data` utilizado por la lógica de este bloque.
      const data = await res.json();
      if (res.ok) {
        setDetail({ open: true, poder: data.poder, loading: false });
        if (data.statusLabels) setStatusLabels(data.statusLabels);
        // Si abrió por primera vez, refrescar lista
        fetchStats();
      } else {
        setDetail({ open: false, poder: null, loading: false });
        showNotif(data.msg || 'No se pudo abrir el expediente.', 'error');
      }
    } catch {
      setDetail({ open: false, poder: null, loading: false });
      showNotif('No se pudo abrir el expediente.', 'error');
    }
  }, [fetch, fetchStats, showNotif]);
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('order');
    if (id && /^[a-f\d]{24}$/i.test(id)) openDetail({ _id: id });
  }, [openDetail]);


  const refreshSignatureFlow = useCallback(async (poder, { skipSignatures = false } = {}) => {
    try {
      if (!skipSignatures && poder.signatureFlowVersion === 'LAWYER_FIRST_V1') {
        const response = await fetch(`${API_URL}/api/docusign/poder-signature-status/${poder._id}`, { headers: { 'x-auth-token': getAuthToken() } });
        if (!response.ok) { const data = await response.json(); throw new Error(data.msg); }
      }
      await openDetail(poder);
      await fetchExpedientes();
    } catch (error) { showNotif(error.message || 'No se pudo verificar el flujo de firmas.', 'error'); }
  }, [fetch, openDetail, showNotif, fetchExpedientes]);

  // Callback `runAction` memoizado para conservar su referencia entre renderizados.
  const runAction = useCallback(async (poderId, action, body = {}) => {
    try {
      // Valor intermedio `res` utilizado por la lógica de este bloque.
      const res = await fetch(`${API_URL}/api/notario-dashboard/expedientes/${poderId}/action`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ action, ...body }),
      });
      // Valor intermedio `data` utilizado por la lógica de este bloque.
      const data = await res.json();
      if (res.ok && data.success) {
        showNotif('Acción registrada correctamente', 'success');
        setDetail(d => ({ ...d, poder: data.poder || d.poder }));
        fetchExpedientes(); fetchStats();
        return true;
      }
      showNotif(data.msg || 'No se pudo ejecutar la acción', 'error');
      return false;
    } catch {
      showNotif('Error de conexión', 'error');
      return false;
    }
  }, [fetch, authHeaders, showNotif, fetchExpedientes, fetchStats]);

  // Callback `toggleChecklist` memoizado para conservar su referencia entre renderizados.
  const toggleChecklist = useCallback(async (poderId, key, value) => {
    try {
      // Valor intermedio `res` utilizado por la lógica de este bloque.
      const res = await fetch(`${API_URL}/api/notario-dashboard/expedientes/${poderId}/checklist`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ key, value }),
      });
      // Valor intermedio `data` utilizado por la lógica de este bloque.
      const data = await res.json();
      if (res.ok && data.success) {
        setDetail(d => ({ ...d, poder: data.poder || d.poder }));
      } else {
        showNotif(data.msg || 'Error al actualizar checklist', 'error');
      }
    } catch { showNotif('Error de conexión', 'error'); }
  }, [fetch, authHeaders, showNotif]);

  // Callback `saveObservations` memoizado para conservar su referencia entre renderizados.
  const saveObservations = useCallback(async (poderId, observations) => {
    try {
      // Valor intermedio `res` utilizado por la lógica de este bloque.
      const res = await fetch(`${API_URL}/api/notario-dashboard/expedientes/${poderId}/observations`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ observations }),
      });
      // Valor intermedio `data` utilizado por la lógica de este bloque.
      const data = await res.json();
      if (res.ok && data.success) {
        showNotif('Observaciones guardadas', 'success');
        setDetail(d => ({ ...d, poder: data.poder || d.poder }));
      } else {
        showNotif(data.msg || 'Error al guardar', 'error');
      }
    } catch { showNotif('Error de conexión', 'error'); }
  }, [fetch, authHeaders, showNotif]);

  // Callback `registerSignature` memoizado para conservar su referencia entre renderizados.
  const registerSignature = useCallback(async (poderId, tipo) => {
    try {
      // Valor intermedio `res` utilizado por la lógica de este bloque.
      const res = await fetch(`${API_URL}/api/notario-dashboard/expedientes/${poderId}/register-signature`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ tipo }),
      });
      // Valor intermedio `data` utilizado por la lógica de este bloque.
      const data = await res.json();
      if (res.ok && data.success) {
        showNotif(`Firma de ${tipo} registrada`, 'success');
        setDetail(d => ({ ...d, poder: data.poder || d.poder }));
      } else {
        showNotif(data.msg || 'Error al registrar firma', 'error');
      }
    } catch { showNotif('Error de conexión', 'error'); }
  }, [fetch, authHeaders, showNotif]);

  return (
    <div className="notario-dashboard">
      {/* Sidebar */}
      <aside className="nd-sidebar">
        <div className="nd-brand">
          <div className="nd-brand-mark"><Icon name="briefcase" /></div>
          <div>
            <div className="nd-brand-title">WISBY</div>
            <div className="nd-brand-sub">Portal de Notarios</div>
          </div>
        </div>

        <nav className="nd-nav">
          <button className={`nd-nav-btn ${tab === 'dashboard' ? 'active' : ''}`} onClick={() => setTab('dashboard')}>
            <Icon name="dashboard" /> Dashboard
          </button>
          <button className={`nd-nav-btn ${tab === 'expedientes' ? 'active' : ''}`} onClick={() => { setTab('expedientes'); fetchExpedientes(); }}>
            <Icon name="folder" /> Expedientes
          </button>
          <button className={`nd-nav-btn ${tab === 'historial' ? 'active' : ''}`} onClick={() => setTab('historial')}>
            <Icon name="clock" /> Historial
          </button>
          <button className={`nd-nav-btn ${tab === 'perfil' ? 'active' : ''}`} onClick={() => setTab('perfil')}>
            <Icon name="user" /> Perfil
          </button>
        </nav>

        <div className="nd-user">
          <div className="nd-user-name">{me.user ? `${me.user.name} ${me.user.lastName || ''}`.trim() : currentUser}</div>
          <div className="nd-user-email">{me.user?.email || ''}</div>
          <button className="nd-logout" onClick={onLogout}><Icon name="logout" /> {adminView ? 'Volver a administración' : 'Cerrar sesión'}</button>
        </div>
      </aside>

      {/* Contenido principal. */}
      <main className="nd-main">
        <details><summary>Agenda de firmas físicas</summary><NotarialCalendar onOpen={openDetail} /></details>
        <header className="nd-topbar">
          <h1>
            {tab === 'dashboard' && 'Dashboard'}
            {tab === 'expedientes' && 'Expedientes asignados'}
            {tab === 'historial' && 'Historial de actividad'}
            {tab === 'perfil' && 'Mi perfil'}
          </h1>
          <span className="nd-topbar-sub">{me.notario?.nombre || ''}{me.notario?.numero ? ` · Notaría No. ${me.notario.numero}` : ''}</span>
        </header>

        {/* DASHBOARD */}
        {tab === 'dashboard' && (
          <section className="nd-content">
            <div className="nd-stats-grid">
              <StatCard label="Total asignados" value={stats.total} accent="primary" />
              <StatCard label="Pendientes" value={stats.pendientes} accent="warn" />
              <StatCard label="En revisión" value={stats.enRevision} accent="info" />
              <StatCard label="Completos" value={stats.completos} accent="success" />
              <StatCard label="Finalizados" value={stats.finalizados} accent="success" />
            </div>

            <div className="nd-card">
              <div className="nd-card-head">
                <h3>Expedientes recientes</h3>
                <button className="nd-link" onClick={() => { setTab('expedientes'); fetchExpedientes(); }}>Ver todos</button>
              </div>
              {loading ? (
                <p className="nd-muted">Cargando...</p>
              ) : expedientes.length === 0 ? (
                <EmptyState text="Aún no tienes expedientes asignados." />
              ) : (
                <ul className="nd-recent-list">
                  {expedientes.slice(0, 5).map(p => (
                    <li key={p._id} onClick={() => openDetail(p)}>
                      <div className="nd-recent-main">
                        <strong>{p.companyName || '—'}</strong>
                        <span className="nd-muted">{p.userName || [p.user?.name, p.user?.lastName].filter(Boolean).join(' ') || '—'} · {NOTARIAL_STAGES[p.notarialWorkflow?.stage] || statusLabels[p.status] || p.status}</span>
                      </div>
                      <span className={`nd-prio ${PRIORIDAD_LABELS[p.prioridad]?.class}`}>{PRIORIDAD_LABELS[p.prioridad]?.label || 'Media'}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        )}

        {/* EXPEDIENTES */}
        {tab === 'expedientes' && (
          <section className="nd-content">
            <div className="nd-card nd-table-card">
              <div className="nd-card-head"><h3>Folios recibidos</h3><button className="nd-link" onClick={fetchExpedientes}>Actualizar</button></div>
              {listError ? <p className="nd-error" role="alert">{listError}</p> : loading ? (
                <p className="nd-muted">Cargando expedientes...</p>
              ) : expedientes.length === 0 ? (
                <EmptyState text="No tienes expedientes asignados todavía. Cuando el administrador te asigne uno, aparecerá aquí automáticamente." />
              ) : (
                <div className="nd-table-wrap">
                  <table className="nd-table">
                    <thead>
                      <tr>
                        <th>Folio</th><th>Empresa</th><th>Cliente</th><th>Tipo de poder</th>
                        <th>Etapa y siguiente paso</th><th>Prioridad</th><th>Recibido</th><th>Visualización</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expedientes.map(p => {
                        // Valor intermedio `tipoPoder` utilizado por la lógica de este bloque.
                        const progress = notaryProcessState(p);
                        const tipoPoder = p.powerSelections?.length ? p.powerSelections.map(selection => selection.customPowerRequest ? 'Poder especial' : selection.powerCatalogId?.displayName || 'Poder').join(', ') : p.answers?.apoderados?.[0]?.tipoPoder || p.answers?.tipoPoder || '—';
                        return (
                          <tr key={p._id}>
                            <td><strong>{p.folio || `#${String(p._id).slice(-6).toUpperCase()}`}</strong></td>
                            <td>{p.companyName || p.answers?.sociedad?.razonSocial || '—'}</td>
                            <td style={{ fontSize: '0.78rem' }}>{p.userName || [p.user?.name, p.user?.lastName].filter(Boolean).join(' ') || '—'}</td>
                            <td style={{ fontSize: '0.75rem' }}>{PODER_LABELS[tipoPoder] || tipoPoder}</td>
                            <td><div className="nd-list-process"><strong>{progress.delivered ? 'Entregado' : progress.current.title}</strong><span>{progress.completed} de {progress.stages.length} pasos · {progress.delivered ? 'Concluido' : progress.current.owner}</span><div aria-hidden="true">{progress.stages.map(step => <i key={step.id} className={step.complete ? 'complete' : step.id === progress.current.id ? 'current' : ''} />)}</div></div></td>
                            <td><span className={`nd-prio ${PRIORIDAD_LABELS[p.prioridad]?.class}`}>{PRIORIDAD_LABELS[p.prioridad]?.label || 'Media'}</span></td>
                            <td style={{ fontSize: '0.72rem', color: '#64748b' }}>{new Date(p.sentToNotarioAt || p.assignedAt || p.createdAt).toLocaleDateString('es-MX')}</td>
                            <td><button className="nd-view-btn" onClick={() => openDetail(p)}><Icon name="eye" /> Visualizar</button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        )}

        {/* HISTORIAL */}
        {tab === 'historial' && (
          <section className="nd-content">
            <div className="nd-card">
              <div className="nd-card-head"><h3>Actividad reciente</h3></div>
              {timelineGlobal.length === 0 ? (
                <EmptyState text="Sin actividad registrada." />
              ) : (
                <ul className="nd-timeline">
                  {timelineGlobal.map((h, i) => (
                    <li key={i}>
                      <div className="nd-timeline-dot" />
                      <div className="nd-timeline-body">
                        <div className="nd-timeline-action">{h.action}</div>
                        <div className="nd-muted">{h.companyName || '—'} · Folio {h.folio || `#${String(h.poderId).slice(-6).toUpperCase()}`}</div>
                        <div className="nd-muted">{new Date(h.performedAt).toLocaleString('es-MX')}{h.performedBy ? ` · ${h.performedBy}` : ''}</div>
                        {h.details && <div className="nd-timeline-details">{h.details}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        )}

        {/* PERFIL */}
        {tab === 'perfil' && (
          <section className="nd-content">
            <div className="nd-card">
              <div className="nd-card-head"><h3>Datos del notario</h3></div>
              <div className="nd-profile-grid">
                <ProfileRow label="Nombre" value={me.notario?.nombre} />
                <ProfileRow label="Notaría No." value={me.notario?.numero} />
                <ProfileRow label="Ciudad" value={me.notario?.ciudad} />
                <ProfileRow label="Estado" value={me.notario?.estado} />
                <ProfileRow label="Correo" value={me.user?.email || me.notario?.email} />
                <ProfileRow label="Teléfono" value={me.notario?.telefono} />
              </div>
              <p className="nd-profile-note">Para actualizar tus datos, contacta al equipo de Wisby.</p>
            </div>
          </section>
        )}
      </main>

      {/* MODAL DE DETALLE (Parte 14, 15, 16, 17) */}
      {detail.open && (
        <NotarioExpedienteDetail
          currentUserId={me.user?._id}
          state={detail}
          onClose={() => setDetail({ open: false, poder: null, loading: false })}
          statusLabels={statusLabels}
          onAction={runAction}
          onToggleChecklist={toggleChecklist}
          onSaveObservations={saveObservations}
          onRegisterSignature={registerSignature}
          onRefresh={refreshSignatureFlow}
        />
      )}

      {notif && <div className={`nd-notif nd-notif-${notif.type}`}>{notif.message}</div>}
    </div>
  );
}

// ================================================================
// SUBCOMPONENTES
// ================================================================
function StatCard({ label, value, accent }) {
  return (
    <div className={`nd-stat-card nd-stat-${accent}`}>
      <div className="nd-stat-value">{value}</div>
      <div className="nd-stat-label">{label}</div>
    </div>
  );
}

// Componente `EmptyState` que renderiza esta parte de la interfaz.
function EmptyState({ text }) {
  return <div className="nd-empty"><p>{text}</p></div>;
}

// Componente `ProfileRow` que renderiza esta parte de la interfaz.
function ProfileRow({ label, value }) {
  return (
    <div className="nd-profile-row">
      <span className="nd-profile-label">{label}</span>
      <span className="nd-profile-value">{value && String(value).trim() ? value : <span style={{ color: '#cbd5e1' }}>—</span>}</span>
    </div>
  );
}

// Componente `NotarioExpedienteDetail` que renderiza esta parte de la interfaz.
function NotarioExpedienteDetail({ currentUserId, state, onClose, statusLabels, onAction, onToggleChecklist, onSaveObservations, onRegisterSignature, onRefresh }) {
  const fetch = useRoleViewFetch();
  // Valores extraídos de una estructura para usarlos directamente en este bloque.
  const { poder, loading } = state;
  // Estado local `observations` y su actualizador, usados para reflejar cambios en la interfaz.
  const [documentError, setDocumentError] = useState('');
  const [activeTab, setActiveTab] = useState('gestion');
  const [preview, setPreview] = useState({ url: '', title: 'Acta liberada', path: '', image: false, loading: false, error: '' });
  const previewUrlRef = useRef('');
  const previewRequestRef = useRef(0);
  const defaultPreviewPathRef = useRef('');
  const closeButtonRef = useRef(null);
  const released = Boolean(poder?.assignedAt || poder?.sentToNotarioAt || poder?.notarialWorkflow?.actReleasedAt);
  const physical = poder?.generalData?.registeredCompany === 'physical';
  const preferredProject = physical && (poder?.notarialDocuments?.find(file => file.key === 'finalProject') || poder?.notarialDocuments?.find(file => file.key === 'project'));
  const defaultPreviewPath = physical ? preferredProject ? `notarial/documents/${preferredProject.key}/view` : 'suggested-project?format=pdf' : 'document';
  const defaultPreviewTitle = physical ? preferredProject ? preferredProject.filename || (preferredProject.key === 'finalProject' ? 'Versión final protocolizada' : 'Proyecto revisado') : PHYSICAL_DEED_TITLE : 'Acta liberada';
  const defaultPreviewReleased = physical ? Boolean(poder?.notarialWorkflow?.actReleasedAt) : released;
  const openPreview = useCallback(async (path, title) => {
    const requestId = ++previewRequestRef.current;
    const endpoint = path.startsWith(`${API_URL}/`) ? path : `${API_URL}/api/notario-dashboard/expedientes/${poder._id}/${path}`;
    setPreview(previous => ({ ...previous, path, title, loading: true, error: '' }));
    try {
      const response = await fetch(endpoint, { headers: { 'x-auth-token': getAuthToken() } });
      if (!response.ok) { const data = await response.json(); throw new Error(data.msg || 'No se pudo abrir el documento.'); }
      const blob = await response.blob();
      if (requestId !== previewRequestRef.current) return;
      const url = URL.createObjectURL(blob);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = url;
      setPreview({ url, path, title, image: blob.type.startsWith('image/'), loading: false, error: '' });
    } catch (error) {
      if (requestId === previewRequestRef.current) setPreview({ url: '', path, title, image: false, loading: false, error: error.message });
    }
  }, [fetch, poder?._id]);
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus?.();
      previewRequestRef.current += 1;
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);
  useEffect(() => {
    if (loading || !defaultPreviewReleased) return;
    const previousDefault = defaultPreviewPathRef.current;
    if (!previousDefault || (previousDefault !== defaultPreviewPath && preview.path === previousDefault)) {
      defaultPreviewPathRef.current = defaultPreviewPath;
      openPreview(defaultPreviewPath, defaultPreviewTitle);
    }
  }, [loading, defaultPreviewReleased, defaultPreviewPath, defaultPreviewTitle, preview.path, openPreview]);
  useEffect(() => {
    const escape = event => { if (event.key === 'Escape' && !document.querySelector('.inw-backdrop') && !document.querySelector('.wisby-leave-backdrop')) guardAdminNavigation(onClose); };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [onClose]);
  const [openingDocument, setOpeningDocument] = useState(false);
  const [observations, setObservations] = useState('');
  // Estado local `rppcDatos` y su actualizador, usados para reflejar cambios en la interfaz.
  const [rppcDatos, setRppcDatos] = useState('');
  useAdminDirty(Boolean(poder) && (observations !== (poder.notarioObservations || '') || rppcDatos !== (poder.inscritoRppcSigerDatos || '')));
  // Estado local `confirmAction` y su actualizador, usados para reflejar cambios en la interfaz.
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    setObservations(poder?.notarioObservations || '');
    setRppcDatos(poder?.inscritoRppcSigerDatos || '');
  }, [poder?._id, poder?.notarioObservations, poder?.inscritoRppcSigerDatos]);

  if (!poder) {
    return (
      <div className="nd-modal-overlay" onClick={() => guardAdminNavigation(onClose)}>
        <div className="nd-modal" onClick={(e) => e.stopPropagation()}>
          <p className="nd-muted">Cargando detalle...</p>
        </div>
      </div>
    );
  }

  const notaryProgress = notaryProcessState(poder);
  // Valor intermedio `answers` utilizado por la lógica de este bloque.
  const answers = poder.answers || {};
  // Valor intermedio `sociedad` utilizado por la lógica de este bloque.
  const sociedad = answers.sociedad || {};
  // Valor intermedio `apoderados` utilizado por la lógica de este bloque.
  const apoderados = Array.isArray(answers.apoderados) ? answers.apoderados : [];
  // Valor intermedio `structuredGrantees` utilizado por la lógica de este bloque.
  const structuredGrantees = Array.isArray(poder.grantees) ? poder.grantees : [];
  // Valor intermedio `structuredSelections` utilizado por la lógica de este bloque.
  const structuredSelections = Array.isArray(poder.powerSelections) ? poder.powerSelections : [];
  // Valor intermedio `organo` utilizado por la lógica de este bloque.
  const organo = answers.organo || {};
  // Valor intermedio `checklist` utilizado por la lógica de este bloque.
  const checklist = poder.checklist || {};
  // Valor intermedio `allowedActions` utilizado por la lógica de este bloque.
  const allowedActions = poder.notarialWorkflow?.actReleasedAt ? [] : ACTIONS_BY_STATUS[poder.status] || [];
  // Valor intermedio `checklistProgress` utilizado por la lógica de este bloque.
  const checklistProgress = Object.values(checklist).filter(Boolean).length;

  // Función `execute` que encapsula una operación reutilizable de este módulo.
  const execute = (action) => {
    if (action === 'notario_register_rppc_siger') {
      onAction(poder._id, action, { data: { datos: rppcDatos } });
    } else {
      onAction(poder._id, action);
    }
    setConfirmAction(null);
  };

  // Función `openFullDocument` que controla la navegación o visibilidad asociada.
  const openDocument = async (path, filename, download = false) => {
    if (!download) return openPreview(path, filename);
    setDocumentError('');
    setOpeningDocument(true);
    try {
      const response = await fetch(`${API_URL}/api/notario-dashboard/expedientes/${poder._id}/${path}`, { headers: { 'x-auth-token': getAuthToken() } });
      if (!response.ok) { const data = await response.json(); throw new Error(data.msg || 'No se pudo obtener el documento.'); }
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      if (download) link.download = filename;
      else { link.target = '_blank'; link.rel = 'noopener noreferrer'; }
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      setDocumentError(error.message || 'No se pudo obtener el documento.');
    } finally { setOpeningDocument(false); }
  };
  const requestedSignatures = [
    { key: 'client', label: 'Cliente', signature: poder.clientSignature },
    { key: 'admin', label: 'Abogado de Wisby', signature: poder.adminSignature },
  ];

  // Componente `InfoRow` que renderiza esta parte de la interfaz.
  const InfoRow = ({ label, value }) => (
    <div className="nd-info-row">
      <span className="nd-info-label">{label}</span>
      <span className="nd-info-value">{value && String(value).trim() ? value : <span style={{ color: '#cbd5e1' }}>—</span>}</span>
    </div>
  );

  return createPortal(
    <div className="nd-case-overlay">
      <div className="nd-case-workspace" role="dialog" aria-modal="true" aria-labelledby="nd-case-title">
        <header className="nd-case-header">
          <img src={logoWisby} alt="Wisby" />
          <div><span className="nd-case-kicker">WISBY · EXPEDIENTE NOTARIAL</span><h2 id="nd-case-title">Folio {poder.folio || `#${String(poder._id).slice(-6).toUpperCase()}`}</h2><p>{poder.companyName || 'Solicitud de poder'} · <span>{poder.notarialWorkflow?.actReleasedAt ? (notaryProgress.delivered ? 'Entregado' : notaryProgress.current.title) : statusLabels[poder.status] || poder.status}</span></p></div>
          {loading && <span className="nd-muted" role="status">Actualizando expediente…</span>}
          <button ref={closeButtonRef} type="button" className="nd-case-close" aria-label="Cerrar expediente" onClick={() => guardAdminNavigation(onClose)}><Icon name="close" /></button>
        </header>
        <div className="nd-case-columns">
        <aside className="nd-case-left" aria-label="Datos y acciones de notaría">
          <nav className="nd-case-tabs" aria-label="Secciones del expediente notarial">{[['gestion', 'Proceso'], ['datos', 'Datos'], ['documentos', `Documentos (${poder.clientDocuments?.length || 0})`], ['firmas', 'Firmas del acta'], ['historial', 'Historial']].map(([key, label]) => <button key={key} type="button" aria-pressed={activeTab === key} className={activeTab === key ? 'active' : ''} onClick={() => setActiveTab(key)}>{label}</button>)}</nav>
        <div className="nd-case-content">
          {/* Estado actual */}
          <div className="nd-status-banner" hidden={activeTab === 'gestion'}>
            <span className={`nd-status nd-status-${poder.status}`}>{NOTARIAL_STAGES[poder.notarialWorkflow?.stage] || statusLabels[poder.status] || poder.status}</span>
            <span className={`nd-prio ${PRIORIDAD_LABELS[poder.prioridad]?.class}`}>Prioridad {PRIORIDAD_LABELS[poder.prioridad]?.label || 'media'}</span>

          </div>

          <div hidden={!['datos', 'documentos'].includes(activeTab)}>          <section className="nd-detail-section">
            <h3>Acta liberada por el equipo legal</h3>
            <p className="nd-muted">Descarga el acta en Word para trabajar con tu equipo o consulta el documento en PDF.</p>
            <div className="nd-actions-row">
              <button className="nd-btn nd-btn-primary" disabled={!released || openingDocument} onClick={() => openDocument('document?format=docx', `${poder.folio || 'acta'}.docx`, true)}>Descargar acta editable (Word)</button>
              <button className="nd-btn nd-btn-secondary" disabled={!released || openingDocument} onClick={() => openDocument('document', `${poder.folio || 'acta'}.pdf`)}>Ver acta en PDF</button>
            </div>
            {!released && <p className="nd-muted">El acta aún está pendiente de liberación por el equipo legal.</p>}
            {documentError && <p className="nd-error" role="alert">{documentError}</p>}
          </section></div>

          <div hidden={activeTab !== 'firmas'}>          {poder.signatureParticipants?.length ? <PowerSignatureProgress poder={poder} onRefresh={() => onRefresh(poder)} /> : <section className="nd-detail-section">
            <div className="nd-section-head"><h3>Firmas solicitadas</h3><button className="nd-btn nd-btn-secondary" onClick={() => onRefresh(poder)}>Actualizar estado</button></div>
            <div className="nd-requested-signatures">
              {requestedSignatures.map(({ key, label, signature }) => <div key={key} className={`nd-firma-card ${signature?.signed ? 'signed' : ''}`}>
                <span>{label}</span>
                <strong>{signature?.signerName || signature?.signerEmail || 'Sin firmante asignado'}</strong>
                {signature?.signerName && signature?.signerEmail && <span className="nd-muted">{signature.signerEmail}</span>}
                <span className={`nd-signature-state ${signature?.signed ? 'is-signed' : ''}`}>{signature?.signed ? 'Firmado' : poder.envelopeId || signature?.signerEmail ? 'Pendiente de firma' : 'Firma no solicitada'}</span>
                {signature?.signed && signature.signedAt && <span className="nd-muted">{new Date(signature.signedAt).toLocaleString('es-MX')}</span>}
              </div>)}
            </div>
            <p className="nd-muted">Se muestra el último estado registrado en Wisby.</p>
          </section>}</div>

          <div hidden={activeTab !== 'documentos'}>          <section className="nd-detail-section">
            <h3>Documentos adjuntos del usuario</h3>
            {poder.clientDocuments?.length ? <ul className="nd-attachment-list">{poder.clientDocuments.map(file => <li key={file.key}>
              <div><strong>{ATTACHMENT_LABELS[file.key] || 'Documento adjunto'}</strong><span className="nd-muted">{file.filename}</span><span className="nd-muted">{file.uploadedAt ? new Date(file.uploadedAt).toLocaleDateString('es-MX') : 'Documento adjunto'}</span></div>
              <div className="nd-actions-row">
                <button className="nd-btn nd-btn-secondary" disabled={openingDocument} onClick={() => openDocument(`attachments/${encodeURIComponent(file.key)}`, file.filename)}>Visualizar</button>
                <button className="nd-btn nd-btn-secondary" disabled={openingDocument} onClick={() => openDocument(`attachments/${encodeURIComponent(file.key)}?download=1`, file.filename, true)}>Descargar</button>
              </div>
            </li>)}</ul> : <p className="nd-muted">No hay documentos adjuntos en este expediente.</p>}
          </section></div>

          <div hidden={activeTab !== 'gestion'}><NotarialWorkflow guided poder={poder} canApproveProject={Boolean(currentUserId && String(poder.notarioId?.userId?._id || poder.notarioId?.userId) === String(currentUserId))} onNavigateSection={setActiveTab} onViewDocument={openPreview} onRefresh={() => onRefresh(poder, { skipSignatures: true })} /></div>

          {/* Información capturada (Parte 14 — solo consulta) */}
          <div hidden={activeTab !== 'datos'}><section className="nd-detail-section">
            <h3>Información del expediente</h3>
            <div className="nd-info-grid">
              <InfoRow label={poder.generalData?.registeredCompany === 'physical' ? 'Persona otorgante' : 'Empresa'} value={poder.companyName || sociedad.razonSocial} />
              <InfoRow label="RFC" value={poder.clientIntake?.rfc || sociedad.rfc} />
              <InfoRow label="Estado" value={sociedad.estado} />
              <InfoRow label="Cliente" value={poder.userName || [poder.user?.name, poder.user?.lastName].filter(Boolean).join(' ')} />
              <InfoRow label="Correo del cliente" value={poder.userEmail || poder.user?.email} />
              <InfoRow label="Órgano otorgante" value={ORGANOS[answers.organoOtorgante] || answers.organoOtorgante} />
              <InfoRow label="Delegado" value={answers.delegado?.nombreCompleto} />
            </div>

            {poder.generalData?.registeredCompany === 'physical' && <><h4 className="nd-subhead">Datos del otorgante</h4><div className="nd-info-grid">{[['grantorNationality', 'Nacionalidad'], ['grantorBirthPlace', 'Lugar de nacimiento'], ['grantorBirthDate', 'Fecha de nacimiento'], ['grantorAddress', 'Domicilio'], ['grantorPostalCode', 'Código postal'], ['grantorPhone', 'Teléfono'], ['grantorEmail', 'Correo'], ['grantorOccupation', 'Ocupación'], ['grantorCivilStatus', 'Estado civil'], ['maritalRegime', 'Régimen matrimonial'], ['grantorIdentityType', 'Documento de identidad'], ['grantorIdentityNumber', 'Número de identificación'], ['grantorIdentityIssuedAt', 'Fecha de expedición'], ['grantorIdentityIssuedPlace', 'Lugar de expedición']].map(([key, label]) => <InfoRow key={key} label={label} value={poder.generalData[key]} />)}</div></>}
            {structuredGrantees.length > 0 && <><h4 className="nd-subhead">Apoderados</h4>{structuredGrantees.map(person => <div className="nd-grantee-summary" key={person.id}><strong>{person.fullName}</strong><div className="nd-info-grid"><InfoRow label="Nacionalidad" value={person.nationality} /><InfoRow label="RFC" value={person.rfc} /><InfoRow label="CURP" value={person.curp} /></div></div>)}</>}
            <h4 className="nd-subhead">Apoderados y poderes</h4>
            {structuredGrantees.length > 0 ? <ul className="nd-apoderados-list">{structuredSelections.map((selection, index) => <li key={index}><strong>{selection.customPowerRequest ? 'Necesito un poder especial' : selection.powerCatalogId?.displayName || 'Poder'}</strong> — {(selection.granteeIds || []).map(id => structuredGrantees.find(person => person.id === id)?.fullName).filter(Boolean).join(', ')}{selection.powerVersionId?.version ? ` · versión ${selection.powerVersionId.version}` : ''}</li>)}</ul> : apoderados.length === 0 ? (
              <p className="nd-muted">—</p>
            ) : (
              <ul className="nd-apoderados-list">
                {apoderados.map((a, i) => (
                  <li key={i}>
                    <strong>{a.nombreCompleto || `Apoderado ${i + 1}`}</strong> — {PODER_LABELS[a.tipoPoder] || a.tipoPoder || '—'}
                    {a.limitar === 'si' && a.limitacionDescripcion ? <div className="nd-muted">Limitación: {a.limitacionDescripcion}</div> : null}
                    {a.tipoPoder === 'otro' && a.otroDescripcion ? <div className="nd-muted">{a.otroDescripcion}</div> : null}
                  </li>
                ))}
              </ul>
            )}

            {answers.organoOtorgante === 'consejo' && organo.consejo?.consejeros?.length > 0 && (
              <>
                <h4 className="nd-subhead">Consejeros</h4>
                <ul className="nd-apoderados-list">
                  {organo.consejo.consejeros.map((c, i) => <li key={i}>{c.nombre} ({c.cargo}){c.correo ? ` · ${c.correo}` : ''}</li>)}
                </ul>
              </>
            )}
            {answers.organoOtorgante === 'asamblea' && organo.asamblea?.accionistas?.length > 0 && (
              <>
                <h4 className="nd-subhead">Accionistas</h4>
                <ul className="nd-apoderados-list">
                  {organo.asamblea.accionistas.map((a, i) => <li key={i}>{a.nombre}{a.correo ? ` · ${a.correo}` : ''}</li>)}
                </ul>
              </>
            )}
          </section></div>

          {/* Checklist documental (Parte 15) */}
          <div hidden={activeTab !== 'gestion' || Boolean(poder.notarialWorkflow?.actReleasedAt)}><section className="nd-detail-section">
            <div className="nd-section-head">
              <h3>Checklist documental</h3>
              <span className="nd-checklist-progress">{checklistProgress}/{Object.keys(CHECKLIST_LABELS).length}</span>
            </div>
            <div className="nd-checklist-grid">
              {Object.entries(CHECKLIST_LABELS).map(([key, label]) => (
                <label key={key} className={`nd-check-item ${checklist[key] ? 'checked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={!!checklist[key]}
                    onChange={(e) => onToggleChecklist(poder._id, key, e.target.checked)}
                  />
                  <span className="nd-check-box">{checklist[key] && <Icon name="check" />}</span>
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </section></div>

          {/* Observaciones (Parte 16) */}
          <div hidden={activeTab !== 'datos'}><section className="nd-detail-section">
            <h3>Observaciones</h3>
            <textarea
              className="nd-textarea"
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              placeholder="Agrega observaciones internas para este expediente..."
              rows={3}
            />
            <button className="nd-btn nd-btn-secondary" onClick={() => onSaveObservations(poder._id, observations)}>
              Guardar observaciones
            </button>
          </section></div>

          {/* Firmas (Parte 16) */}
          <div hidden={activeTab !== 'firmas'}><section className="nd-detail-section">
            <h3>Firmas registradas</h3>
            <div className="nd-firmas-row">
              <div className={`nd-firma-card ${poder.firmaDelegadoRegistrada ? 'signed' : ''}`}>
                <span>Firma del Delegado</span>
                <strong>{poder.firmaDelegadoRegistrada ? 'Registrada' : 'Pendiente'}</strong>
                <button className="nd-btn nd-btn-secondary" disabled={poder.firmaDelegadoRegistrada}
                  onClick={() => onRegisterSignature(poder._id, 'delegado')}>Registrar</button>
              </div>
              <div className={`nd-firma-card ${poder.firmaNotarioRegistrada ? 'signed' : ''}`}>
                <span>Firma del Notario</span>
                <strong>{poder.firmaNotarioRegistrada ? 'Registrada' : 'Pendiente'}</strong>
                <button className="nd-btn nd-btn-secondary" disabled={poder.firmaNotarioRegistrada}
                  onClick={() => onRegisterSignature(poder._id, 'notario')}>Registrar</button>
              </div>
            </div>
          </section></div>

          {/* Acciones (Parte 16) */}
          <div hidden={activeTab !== 'gestion' || Boolean(poder.notarialWorkflow?.actReleasedAt)}><section className="nd-detail-section">
            <h3>Acciones</h3>
            {allowedActions.length === 0 ? (
              <p className="nd-muted">No hay acciones disponibles en el estado actual del expediente.</p>
            ) : (
              <div className="nd-actions-row">
                {allowedActions.map(act => (
                  <button
                    key={act.action}
                    className={`nd-btn nd-btn-${act.kind}`}
                    onClick={() => setConfirmAction(act)}
                  >
                    {act.label}
                  </button>
                ))}
              </div>
            )}

            {confirmAction && (
              <div className="nd-confirm-inline">
                <span>¿Confirmas: <strong>{confirmAction.label}</strong>?</span>
                <button className="nd-btn nd-btn-primary" onClick={() => execute(confirmAction.action)}>Sí, continuar</button>
                <button className="nd-btn nd-btn-ghost" onClick={() => setConfirmAction(null)}>Cancelar</button>
              </div>
            )}

            {confirmAction?.action === 'notario_register_rppc_siger' && (
              <input
                className="nd-input"
                placeholder="Datos de inscripción (folio, fecha, registro...)"
                value={rppcDatos}
                onChange={(e) => setRppcDatos(e.target.value)}
              />
            )}
          </section></div>

          {/* Timeline (Parte 17) */}
          <div hidden={activeTab !== 'historial'}><section className="nd-detail-section">
            <h3>Historial del expediente</h3>
            {poder.history && poder.history.length > 0 ? (
              <ul className="nd-timeline">
                {[...poder.history].reverse().map((h, i) => (
                  <li key={i}>
                    <div className="nd-timeline-dot" />
                    <div className="nd-timeline-body">
                      <div className="nd-timeline-action">{h.action}</div>
                      <div className="nd-muted">{new Date(h.performedAt).toLocaleString('es-MX')}{h.performedBy ? ` · ${h.performedBy}` : ''}</div>
                      {h.details && <div className="nd-timeline-details">{h.details}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="nd-muted">Sin eventos registrados.</p>
            )}
          </section></div>
        </div>
        </aside>
        <section className="nd-case-right" aria-label="Visualizador de archivos">
          <div className="nd-case-view-toolbar"><div><span>DOCUMENTO DEL EXPEDIENTE</span><strong>{preview.title}</strong></div><button type="button" className="nd-btn nd-btn-secondary" disabled={!released || preview.loading} onClick={() => openPreview('document', 'Acta liberada')}>Ver acta</button></div>
          <div className="nd-case-document" aria-busy={preview.loading}>
            {preview.loading ? <div className="nd-case-placeholder" role="status"><span className="nd-case-spinner" />Cargando documento…</div>
              : preview.error ? <div className="nd-case-placeholder" role="alert"><strong>No se pudo abrir el documento</strong><p>{preview.error}</p><button type="button" className="nd-btn nd-btn-primary" onClick={() => openPreview(preview.path, preview.title)}>Reintentar</button></div>
              : preview.url ? preview.image ? <div className="nd-case-image"><img src={preview.url} alt={preview.title} /></div> : <iframe src={preview.url} title={`Visualizador: ${preview.title}`} />
              : <div className="nd-case-placeholder"><Icon name="folder" /><strong>{released ? 'Selecciona un documento' : 'Acta pendiente de liberación'}</strong><p>El acta y los archivos que selecciones se mostrarán aquí.</p></div>}
          </div>
        </section>
        </div>
      </div>
    </div>, document.body
  );
}
